import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from "../apiClient";
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format } from 'date-fns';
import Select from 'react-select';
import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import config from '../config';

function IVMRReportPage() {
  const { user } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [reports, setReports] = useState([]); // Array of loaded reports
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [abortController, setAbortController] = useState(null); // <-- fix
  const [committedStartDate, setCommittedStartDate] = useState(null);
  const [committedEndDate, setCommittedEndDate] = useState(null);
  const [pendingVehicles, setPendingVehicles] = useState([]); // Vehicles still loading
  const [reportRequested, setReportRequested] = useState(false); // <-- add
  const [skippedVehicles, setSkippedVehicles] = useState([])
  const isMountedRef = useRef(true);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => { 
      isMountedRef.current = false;
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (loading && startTime) {
      timerRef.current = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [loading, startTime]);

  // Load vehicles on component mount
  useEffect(() => {
    const loadVehicles = async () => {
      try {
        const res = await api.get('/api/vehicles');
        const vehicleOptions = res.data.map(v => ({
          value: v.id,
          label: v.number
        }));
        setVehicles(vehicleOptions);
      } catch (err) {
        console.error('Failed to load vehicles:', err);
        setError('Failed to load vehicles. Please refresh and try again.');
      }
    };

    loadVehicles();
  }, []);

  // Listen for token refresh events
  useEffect(() => {
    const handleTokenRefresh = async () => {
      if (user && user.refreshToken) {
        await user.refreshToken();
      }
    };

    window.addEventListener('tokenRefreshNeeded', handleTokenRefresh);
    return () => window.removeEventListener('tokenRefreshNeeded', handleTokenRefresh);
  }, [user]);

  useEffect(() => {
    if (pendingVehicles.length === 0 && loading) {
      setLoading(false);
      setAbortController(null);
    }
  }, [pendingVehicles.length, loading]);

  const handleDateChange = useCallback((update) => {
    setDateRange(update);
  }, []);

  const handleCancel = useCallback(() => {
    if (abortController) {
      abortController.abort();
    }
    setLoading(false);
    setStartTime(null);
    setElapsedTime(0);
  }, [abortController]);


  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setReportRequested(true);

    if (selectedVehicles.length === 0) return setError("Select at least one vehicle");
    if (!startDate || !endDate) return setError("Select a date range");
    if (startDate > endDate) return setError("Start date must be before end date");
    if (Math.ceil((endDate - startDate) / (1000*60*60*24)) > 90)
      return setError("Date range cannot exceed 90 days");

    // Set committed dates for the common header
    setCommittedStartDate(startDate);
    setCommittedEndDate(endDate);

    setReports([]);
    setSkippedVehicles([]);
    setPendingVehicles(selectedVehicles.map(v => v.value));
    setError("");
    setLoading(true);
    setStartTime(Date.now());
    setElapsedTime(0);

    const controller = new AbortController();
    setAbortController(controller);

    const formattedStart = format(startDate, "yyyy-MM-dd");
    const formattedEnd = format(endDate, "yyyy-MM-dd");
    const concurrency = 2;
    let index = 0;

    const runNext = async () => {
      if (index >= selectedVehicles.length || !isMountedRef.current) return;
      const vehicle = selectedVehicles[index];
      index++;

      try {
        const res = await api.post("/api/ivmrreport", {
          vehicle: vehicle.value,
          start_date: formattedStart,
          end_date: formattedEnd
        }, { signal: controller.signal });

        if (!isMountedRef.current) return;

        if (res.data.report) {
          setReports(prev => [...prev, res.data.report]);
        }

        if (res.data.skipped) {
          setSkippedVehicles(prev => [...prev, vehicle.label]);
        }

        setPendingVehicles(prev => prev.filter(id => id !== vehicle.value));
      } catch (err) {
        if (!isMountedRef.current) return;

        if (err.name === "AbortError") setError("Report generation cancelled.");
        else setError(`Error for ${vehicle.label}: ${err.message}`);

        setPendingVehicles(prev => prev.filter(id => id !== vehicle.value));
      }

      await runNext();
    };

    const workers = Array(Math.min(concurrency, selectedVehicles.length)).fill(null).map(() => runNext());
    await Promise.all(workers);

    if (isMountedRef.current) {
      setLoading(false);
      setStartTime(null);  // Add this line
    }
  };

  const handleSelectAllToggle = useCallback(() => {
    if (selectedVehicles.length === vehicles.length) {
      setSelectedVehicles([]); // Deselect all
    } else {
      setSelectedVehicles(vehicles); // Select all
    }
  }, [selectedVehicles.length, vehicles]);

  const exportEachToExcel = useCallback(() => {
    if (!reports || reports.length === 0) return;
    reports.forEach(entry => exportVehicleToExcel(entry));
  }, [reports]);

  const exportEachToPDF = useCallback(() => {
    if (!reports || reports.length === 0) return;
    reports.forEach(entry => exportVehicleToPDF(entry));
  }, [reports]);

  function estimateRowHeight(text, columnWidthChars, fontSize = 7, lineHeightFactor = 0.7) {
    if (!text) return 15; 

    const avgCharsPerLine = columnWidthChars || 40;
    const lineCount = text.split('\n').reduce((total, line) => {
      return total + Math.ceil(line.length / avgCharsPerLine);
    }, 0);

    const baseLineHeight = fontSize * lineHeightFactor;
    return Math.max(15, lineCount * baseLineHeight);
  }

  const exportVehicleToExcel = async (entry) => {
    try {
      const workbook = new ExcelJS.Workbook();

      // Load template
      const response = await fetch(`${config.API_BASE_URL}/api/ivmrreport-template/${user?.profile?.companyId || 'default'}`);
      if (!response.ok) {
        throw new Error("Template not found or cannot be loaded.");
      }

      const arrayBuffer = await response.arrayBuffer();
      await workbook.xlsx.load(arrayBuffer);

      const sheet = workbook.worksheets[0];
      if (!sheet) {
        throw new Error("Template does not contain any sheets.");
      }

      const safeStart = new Date(committedStartDate);
      const safeEnd = new Date(committedEndDate);
      if (isNaN(safeStart) || isNaN(safeEnd)) {
        throw new Error("Invalid date format.");
      }

      sheet.name = entry.vehicle_number;

      // Fill header fields
      sheet.getCell('C2').value = entry.group_names || 'N/A';
      sheet.getCell('H2').value = Number(entry.total_miles ?? 0);
      sheet.getCell('C3').value = `${entry.vehicle_number ?? ''} (${entry.vehicle_id ?? ''})`;
      sheet.getCell('H3').value = Number(entry.adjusted_start_odometer ?? 0);
      sheet.getCell('C4').value = entry.domicile ?? '';
      sheet.getCell('H4').value = Number(entry.adjusted_end_odometer ?? 0);
      sheet.getCell('C5').value = entry.driver_names || '';
      sheet.getCell('C6').value = `${format(safeStart, 'M/d/yyyy')} to ${format(safeEnd, 'M/d/yyyy')}`;
      sheet.getCell('H6').value = entry.states || '';

      // Template row to copy styles from
      const templateRow = sheet.getRow(10);
      let rowIndex = 10;

      let prevEndOdo = null;

      entry.trips.forEach((trip, i) => {
        if (i > 0) {
          sheet.insertRow(rowIndex, [], 'i');
        }

        const row = sheet.getRow(rowIndex++);

        // Copy styles from templateRow
        templateRow.eachCell({ includeEmpty: true }, (templateCell, colNumber) => {
          const cell = row.getCell(colNumber);
          cell.style = { ...templateCell.style };
          if (templateCell.fill) cell.fill = { ...templateCell.fill };
          if (templateCell.border) cell.border = { ...templateCell.border };
        });

        // Fill data
        const [y, m, d] = trip.date.split('-').map(Number);
        const tripDate = new Date(y, m - 1, d);
        row.getCell(2).value = trip.day;
        row.getCell(3).value = format(tripDate, 'M/d/yyyy');
        row.getCell(4).value = Number(trip.adjusted_start_odometer ?? 0);
        row.getCell(5).value = Number(trip.adjusted_end_odometer ?? 0);
        row.getCell(6).value = Number(trip.distance);
        row.getCell(7).value = trip.state || '';
        const roadText = trip.road_names || '';
        const roadCell = row.getCell(8);
        roadCell.value = roadText;
        roadCell.alignment = { wrapText: true, vertical: 'top', horizontal: 'left' };
        row.height = estimateRowHeight(roadText, sheet.getColumn(8).width || 20, 7);

        // --- Highlight discrepancy ---
        if (prevEndOdo !== null && prevEndOdo !== trip.adjusted_start_odometer) {
          // Highlight whole row (light red fill)
          row.eachCell(cell => {
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'FFFFCCCC' }
            };
          });
        }

        prevEndOdo = trip.adjusted_end_odometer;
        row.commit();
      });

      // Export
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      // Make domicile safe for filenames
      const domicileSafe = entry.domicile ? entry.domicile.replace(/[^a-zA-Z0-9]/g, '_') : 'Unknown';

      saveAs(
        blob,
        `${domicileSafe}_vehicle_${entry.vehicle_number}_${format(safeStart, 'yyyy-MM-dd')}_to_${format(safeEnd, 'yyyy-MM-dd')}.xlsx`
      );
    } catch (err) {
      console.error('Excel export error:', err);
      setError(`Failed to export Excel: ${err.message}`);
    }
  };

  const exportVehicleToPDF = (entry) => {
    try {
      const doc = new jsPDF();
      const lineHeight = 8;
      let y = 15;

      doc.setFontSize(14);
      doc.text('Individual Vehicle Mileage Report', 14, y);
      y += lineHeight;

      doc.setFontSize(11);
      doc.text(`Company: ${entry.group_names}.`, 14, y);
      doc.text(`Total Miles: ${entry.total_miles}`, 150, y);
      y += lineHeight;
      doc.text(`Truck: ${entry.vehicle_number} (${entry.vehicle_id})`, 14, y);
      doc.text(`Starting Odo.: ${entry.adjusted_start_odometer}`, 150, y);
      y += lineHeight;
      doc.text(`Home Station: ${entry.domicile}`, 14, y);
      doc.text(`Ending Odo.: ${entry.adjusted_end_odometer}`, 150, y);
      y += lineHeight;
      doc.text(`Driver(s): ${entry.driver_names}`, 14, y);
      y += lineHeight;
      doc.text(`Dates: ${format(committedStartDate, 'M/d/yyyy')} to ${format(committedEndDate, 'M/d/yyyy')}`, 14, y);
      doc.text(`States: ${entry.states}`, 150, y);
      y += 10;

      // Table body
      const tableBody = entry.trips.map(trip => [
        trip.day,
        trip.date,
        trip.adjusted_start_odometer,
        trip.adjusted_end_odometer,
        trip.distance,
        trip.state,
        trip.road_names
      ]);

      doc.autoTable({
        startY: y,
        head: [['Day', 'Date', 'Odo. Start', 'Odo. End', 'Miles', 'State', 'Roads']],
        body: tableBody,
        theme: 'striped',
        headStyles: { fillColor: [22, 160, 133] },
        didParseCell: function (data) {
          // Only body rows
          if (data.section === 'body') {
            const rowIndex = data.row.index;
            const colIndex = data.column.index;

            // Only check Odo Start column (index 2)
            if (colIndex === 2 && rowIndex > 0) {
              const prevEndOdo = tableBody[rowIndex - 1][3]; // previous row Odo End
              const currStartOdo = data.cell.raw;

              if (prevEndOdo !== currStartOdo) {
                // Highlight entire row in light red
                for (let i = 0; i < data.table.columns.length; i++) {
                  data.table.body[rowIndex].cells[i].styles.fillColor = [255, 204, 204]; // light red
                }
              }
            }
          }
        }
      });

      const domicileSafe = entry.domicile ? entry.domicile.replace(/[^a-zA-Z0-9]/g, '_') : 'Unknown';
      doc.save(`${domicileSafe}_vehicle_${entry.vehicle_number}_${format(committedStartDate, 'yyyy-MM-dd')}_to_${format(committedEndDate, 'yyyy-MM-dd')}.pdf`);
    } catch (err) {
      console.error('PDF export error:', err);
      setError(`Failed to export PDF: ${err.message}`);
    }
  };

  // Loading state while checking authentication
  if (!user) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <form onSubmit={handleSubmit} className="mb-4">
        <div className="mb-3">
          <label className="form-label" style={{ marginRight: "10px" }}>Select Date Range:</label>
          <DatePicker
            selectsRange
            startDate={startDate}
            endDate={endDate}
            onChange={handleDateChange}
            dateFormat="yyyy-MM-dd"
            className="form-control"
            disabled={loading}
            placeholderText="Select date range"
            maxDate={new Date()}
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Select Vehicles:</label>
            <div className="d-flex align-items-start gap-2">
              <div style={{ flexGrow: 1 }}>
                <Select
                  options={vehicles}
                  value={selectedVehicles}
                  onChange={setSelectedVehicles}
                  isMulti
                  className="basic-multi-select"
                  classNamePrefix="select"
                  isDisabled={loading}
                  placeholder="Choose vehicles..."
                  noOptionsMessage={() => "No vehicles available"}
                />
              </div>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm mt-1"
                onClick={handleSelectAllToggle}
                disabled={loading || vehicles.length === 0}
              >
                {selectedVehicles.length === vehicles.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>
        </div>
        <div className="d-flex gap-2">
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Generating...' : 'Generate Report'}
          </button>
          {loading && (
            <button type="button" className="btn btn-danger" onClick={handleCancel}>
              Cancel
            </button>
          )}
        </div>
      </form>

      {error && (
        <div className="alert alert-danger alert-dismissible" role="alert">
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError('')}
            aria-label="Close"
          ></button>
        </div>
      )}

      {/* Progress & spinner */}
      {pendingVehicles.length > 0 && (
        <div className="text-center my-4">
          <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 mb-1">
            Processing vehicle reports: {reports.length} of {reports.length + pendingVehicles.length} completed
          </p>
          <p className="text-muted">
            <strong>Time Elapsed: {formatTime(elapsedTime)}</strong>
          </p>
          {reports.length > 0 && (
            <p className="text-muted small">
              Est. time remaining: ~{formatTime(Math.ceil((elapsedTime / reports.length) * pendingVehicles.length))}
            </p>
          )}
        </div>
      )}

      {/* Skipped vehicles */}
      {skippedVehicles.length > 0 && (
        <div className="alert alert-warning mt-4">
          <strong>Skipped vehicles due to license limits:</strong>
          <ul>
            {skippedVehicles.map(v => <li key={v}>{v}</li>)}
          </ul>
        </div>
      )}

      {reports.length > 0 && (
        <div>
          {/* Common Report Header */}
          {committedStartDate && committedEndDate && (
            <h4 className="mt-4">
              IVMR from {format(committedStartDate, 'yyyy-MM-dd')} to {format(committedEndDate, 'yyyy-MM-dd')}
            </h4>
          )}

          <div className="mb-3 d-flex gap-2">
            <button className="btn btn-outline-success" onClick={exportEachToExcel}>
              Export Each to Excel
            </button>
            <button className="btn btn-outline-danger" onClick={exportEachToPDF}>
              Export Each to PDF
            </button>
          </div>

          {reports.map((entry, idx) => {
            const isPending = pendingVehicles.includes(entry.vehicle_id);

            return (
              <div
                key={idx}
                className={`mb-5 p-2 ${isPending ? "border border-primary rounded" : ""}`}
                style={{ backgroundColor: isPending ? "#e6f2ff" : "inherit" }}
              >
                <h5>
                  IVMR for Vehicle #{entry.vehicle_number}{" "}
                  {isPending && <span className="text-primary"> - Processing...</span>}
                </h5>

                {/* Header Section */}
                <div className="border p-3 mb-3" style={{ fontFamily: 'monospace' }}>
                  <div className="row">
                    <div className="col-md-6">
                      <div><strong>Group:</strong> {entry.group_names || 'N/A'}</div>
                      <div><strong>Truck:</strong> {entry.vehicle_number} ({entry.vehicle_id})</div>
                      <div><strong>Home Station:</strong> {entry.domicile}</div>
                      <div><strong>Driver(s):</strong> {entry.driver_names}</div>
                      <div>
                        <strong>Dates:</strong>{' '}
                        {committedStartDate && committedEndDate
                          ? `${format(committedStartDate, 'M/d/yyyy')} to ${format(committedEndDate, 'M/d/yyyy')}`
                          : 'N/A'}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div><strong>Total Miles:</strong> {entry.total_miles}</div>
                      <div><strong>Starting Odo.:</strong> {entry.adjusted_start_odometer}</div>
                      <div><strong>Ending Odo.:</strong> {entry.adjusted_end_odometer}</div>
                      <div><strong>States:</strong> {entry.states}</div>
                    </div>
                  </div>
                </div>

                {/* Export Buttons */}
                <div className="mb-2 d-flex gap-2">
                  <button className="btn btn-sm btn-outline-success" onClick={() => exportVehicleToExcel(entry)}>
                    Export to Excel
                  </button>
                  <button className="btn btn-sm btn-outline-danger" onClick={() => exportVehicleToPDF(entry)}>
                    Export to PDF
                  </button>
                </div>

                {/* Data Table */}
                <div className="table-responsive">
                  <table className="table table-striped table-hover">
                    <thead className="table-dark">
                      <tr>
                        <th>Day</th>
                        <th>Date</th>
                        <th>State</th>
                        <th>Distance (mi)</th>
                        <th>Start Odo.</th>
                        <th>End Odo.</th>
                        <th>Roads</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entry.trips.map((trip, i) => {
                        const prevTrip = i > 0 ? entry.trips[i - 1] : null;
                        const discrepancy = prevTrip && prevTrip.adjusted_end_odometer !== trip.adjusted_start_odometer;
                        const cellStyle = { backgroundColor: discrepancy ? "#ffcccc" : "inherit" };

                        return (
                          <tr key={i} className={discrepancy ? "table-warning" : ""}>
                            <td style={cellStyle}>{trip.day}</td>
                            <td style={cellStyle}>{trip.date}</td>
                            <td style={cellStyle}>{trip.state}</td>
                            <td style={cellStyle}>{trip.distance}</td>
                            <td style={cellStyle}>{trip.adjusted_start_odometer}</td>
                            <td style={cellStyle}>{trip.adjusted_end_odometer}</td>
                            <td style={cellStyle} className="text-truncate" title={trip.road_names}>
                              {trip.road_names}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Only show "No data found" if report was requested, not loading, not pending, and no reports */}
      {!loading && pendingVehicles.length === 0 && reportRequested && reports.length === 0 && (
        <div className="alert alert-info">
          No data found for the selected vehicles and date range.
        </div>
      )}
    </div>
  );
}

export default IVMRReportPage;